import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { extractApiMessage } from "@/shared/api/extract-message";
import { toastError, toastExito } from "@/lib/toast";
import {
  atenderSolicitudCatalogo,
  contarSolicitudesCatalogoPendientes,
  eliminarSolicitudCatalogoBandeja,
  listarSolicitudesCatalogo,
  misTiposResponsableCatalogo,
  rechazarSolicitudCatalogo,
  type ListarSolicitudesCatalogoParams,
} from "@/features/solicitudes-catalogo/services/solicitudes-catalogo.api";

/**
 * Bandeja PAGINADA server-side · estado/tipo/búsqueda/página forman parte de la key
 * (extiende qk.bandejaSolicitudesCatalogo para que los invalidate alcancen por prefijo).
 */
export const bandejaSolicitudesCatalogoQueryOptions = (params: ListarSolicitudesCatalogoParams = {}) =>
  queryOptions({
    queryKey: [...qk.bandejaSolicitudesCatalogo, "paginado", params] as const,
    queryFn: () => listarSolicitudesCatalogo(params),
    placeholderData: keepPreviousData,
  });

export function useBandejaSolicitudesCatalogo(params: ListarSolicitudesCatalogoParams = {}) {
  return useQuery(bandejaSolicitudesCatalogoQueryOptions(params));
}

/**
 * Tipos que el usuario atiende (por rol · configurable por el administrador). Vacío =
 * no tiene bandeja: el nav oculta la entrada y la página muestra «Acceso restringido».
 */
export const misTiposResponsableCatalogoQueryOptions = queryOptions({
  queryKey: qk.misTiposResponsableCatalogo,
  queryFn: misTiposResponsableCatalogo,
  staleTime: 60_000,
});

export function useMisTiposResponsableCatalogo(enabled = true) {
  return useQuery({ ...misTiposResponsableCatalogoQueryOptions, enabled });
}

/** Contador de pendientes · badge del nav. Polling suave para que lleguen sin refrescar. */
export function useSolicitudesCatalogoPendientes(enabled: boolean) {
  return useQuery({
    queryKey: qk.solicitudesCatalogoPendientes,
    queryFn: contarSolicitudesCatalogoPendientes,
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useResolverSolicitudCatalogo() {
  const qc = useQueryClient();
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: qk.bandejaSolicitudesCatalogo });
  };

  const atender = useMutation({
    mutationFn: ({ id, comentario }: { id: string; comentario?: string }) => atenderSolicitudCatalogo(id, comentario),
    onSuccess: () => {
      invalidar();
      toastExito({ title: "Solicitud atendida", message: "Quedó marcada como atendida." });
    },
    onError: (error: unknown) => {
      toastError({ title: "No se pudo atender", message: extractApiMessage(error, "Intenta de nuevo.") });
    },
  });

  const rechazar = useMutation({
    mutationFn: ({ id, comentario }: { id: string; comentario?: string }) => rechazarSolicitudCatalogo(id, comentario),
    onSuccess: () => {
      invalidar();
      toastExito({ title: "Solicitud rechazada", message: "El solicitante verá el motivo." });
    },
    onError: (error: unknown) => {
      toastError({ title: "No se pudo rechazar", message: extractApiMessage(error, "Intenta de nuevo.") });
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => eliminarSolicitudCatalogoBandeja(id),
    onSuccess: () => {
      invalidar();
      toastExito({ title: "Solicitud eliminada", message: "Se quitó de tu bandeja · el solicitante conserva su aviso." });
    },
    onError: (error: unknown) => {
      toastError({ title: "No se pudo eliminar", message: extractApiMessage(error, "Intenta de nuevo.") });
    },
  });

  return { atender, rechazar, eliminar };
}
